import { useCurrencyStore } from '../stores/currencyStore';

/**
 * Hook for formatting prices in the user's selected currency
 *
 * @example
 * const { formatPrice, currency, currencySymbol } = usePrice();
 * <span>{formatPrice(29.99)}</span> // Shows "$29.99" or "Rp 475,414" etc.
 */
export function usePrice() {
  const {
    currency,
    formatPrice,
    convertFromUSD,
    getCurrencyInfo,
    setCurrency,
    resetToDetected,
    isManuallySet,
    detectedCurrency,
  } = useCurrencyStore();

  const currencyInfo = getCurrencyInfo();

  return {
    // Current currency code (e.g., 'USD', 'IDR')
    currency,
    // Currency symbol (e.g., '$', 'Rp')
    currencySymbol: currencyInfo.symbol,
    // Currency name (e.g., 'US Dollar', 'Indonesian Rupiah')
    currencyName: currencyInfo.name,
    // Format a USD price to the selected currency
    formatPrice,
    // Convert USD amount to selected currency (just the number)
    convertFromUSD,
    // Set currency manually
    setCurrency,
    // Reset to detected currency
    resetToDetected,
    // Whether user has manually selected a currency
    isManuallySet,
    // The auto-detected currency
    detectedCurrency,
  };
}

/**
 * Component wrapper for displaying prices
 *
 * @example
 * <Price amount={29.99} />
 * <Price amount={29.99} showOriginal /> // Shows "Rp 475,414 ($29.99)"
 */
export function Price({
  amount,
  showOriginal = false,
  className = '',
}: {
  amount: number;
  showOriginal?: boolean;
  className?: string;
}) {
  const { formatPrice } = usePrice();
  return <span className={className}>{formatPrice(amount, showOriginal)}</span>;
}
